import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, BedDouble, MapPin, Waves, Car, Wifi, Users } from "lucide-react";
import { Layout } from "../components/Layout";
import { SponsorSlot } from "../components/SponsorSlot";
import { eur } from "../lib/format";
import * as api from "../api";

const ACC_TYPES = [["", "Tutte le tipologie"], ["appartamento", "Appartamento"], ["casa_vacanze", "Casa vacanze"],
  ["bb", "B&B"], ["affittacamere", "Affittacamere"], ["hotel", "Hotel"], ["residence", "Residence"],
  ["agriturismo", "Agriturismo"], ["villaggio", "Villaggio"], ["altro", "Altro"]];
const AMENITY_FILTERS = [["wifi", "Wi-Fi"], ["parking", "Parcheggio"], ["pool", "Piscina"], ["ac", "Aria condizionata"],
  ["kitchen", "Cucina"], ["breakfast", "Colazione"], ["sea_view", "Vista mare"], ["pets", "Animali ammessi"], ["accessibility", "Accessibilità"]];

function useDestinationOptions() {
  return useQuery({
    queryKey: ["destOptions"],
    queryFn: async () => {
      const terrs = await api.territories();
      const opts = [["", "Tutte le destinazioni"]];
      for (const t of terrs) {
        opts.push([t.slug, `${t.name} (territorio)`]);
        try {
          const { destinations } = await api.territoryDestinations(t.slug);
          for (const d of destinations) opts.push([d.slug, `\u00A0\u00A0${d.name}`]);
        } catch (e) { /* ignore */ }
      }
      return opts;
    },
  });
}

export default function Accommodations() {
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState({
    destination: params.get("destination") || "",
    checkin: params.get("checkin") || "",
    checkout: params.get("checkout") || "",
    guests: params.get("guests") || "",
    type: params.get("type") || "",
    price_min: params.get("price_min") || "",
    price_max: params.get("price_max") || "",
  });
  const [amenities, setAmenities] = useState(() => AMENITY_FILTERS.reduce((o, [k]) => ({ ...o, [k]: params.get(k) === "true" }), {}));
  const query = {};
  ["destination", "checkin", "checkout", "guests", "type", "price_min", "price_max"].forEach((k) => { if (params.get(k)) query[k] = params.get(k); });
  AMENITY_FILTERS.forEach(([k]) => { if (params.get(k) === "true") query[k] = true; });

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["accSearch", params.toString()],
    queryFn: () => api.searchAccommodations(query),
  });
  const doSearch = () => {
    const next = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => { if (v) next.set(k, v); });
    Object.entries(amenities).forEach(([k, v]) => { if (v) next.set(k, "true"); });
    setParams(next);
  };
  const toggleAmenity = (k) => setAmenities((a) => ({ ...a, [k]: !a[k] }));
  const activeFilterCount = Object.values(amenities).filter(Boolean).length;
  const { data: destOptions = [["", "Tutte le destinazioni"]] } = useDestinationOptions();

  return (
    <Layout noPadding>
      <section className="bg-[#0A1128] text-white" data-testid="accommodations-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-secondary mb-4">Dove dormire</div>
          <h1 className="font-heading font-semibold text-4xl sm:text-6xl leading-[0.98] tracking-tight max-w-2xl">Il tuo rifugio nel Gargano.</h1>
          <p className="mt-5 text-lg text-white/80 max-w-xl">Case, appartamenti e strutture selezionate. Cerca le date e trova subito ciò che è davvero disponibile.</p>
        </div>
      </section>

      {/* search bar */}
      <div className="sticky top-16 z-20 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Destinazione</span>
              <select data-testid="acc-search-destination" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm">
                {destOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Check-in</span>
              <input data-testid="acc-search-checkin" type="date" value={form.checkin} onChange={(e) => setForm({ ...form, checkin: e.target.value })} className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm" /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Check-out</span>
              <input data-testid="acc-search-checkout" type="date" value={form.checkout} onChange={(e) => setForm({ ...form, checkout: e.target.value })} className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm" /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Ospiti</span>
              <input data-testid="acc-search-guests" type="number" min="1" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} placeholder="2" className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm" /></label>
            <button data-testid="acc-search-btn" onClick={doSearch} className="rounded-full bg-primary text-white font-semibold px-6 py-2.5 flex items-center justify-center gap-2 hover:bg-primary/90"><Search className="h-4 w-4" /> Cerca</button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Tipologia</span>
              <select data-testid="acc-search-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm">
                {ACC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Prezzo min €</span>
              <input data-testid="acc-search-pricemin" type="number" min="0" value={form.price_min} onChange={(e) => setForm({ ...form, price_min: e.target.value })} className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm" /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Prezzo max €</span>
              <input data-testid="acc-search-pricemax" type="number" min="0" value={form.price_max} onChange={(e) => setForm({ ...form, price_max: e.target.value })} className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm" /></label>
            <div className="text-xs text-slate-400 self-center">{activeFilterCount > 0 ? `${activeFilterCount} servizi selezionati` : "Filtra per servizi ↓"}</div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="acc-amenity-filters">
            {AMENITY_FILTERS.map(([k, l]) => (
              <button key={k} data-testid={`acc-filter-${k}`} onClick={() => toggleAmenity(k)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${amenities[k] ? "bg-primary text-white border-primary" : "bg-white text-slate-500 border-slate-200 hover:border-primary/40"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {isLoading ? <div className="text-center py-20 text-slate-400">Caricamento...</div> : results.length === 0 ? (
          <div className="text-center py-24 max-w-lg mx-auto" data-testid="acc-empty-state">
            <div className="h-20 w-20 rounded-3xl bg-primary/8 text-primary flex items-center justify-center mx-auto mb-6"><BedDouble className="h-9 w-9" /></div>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">Stiamo preparando le camere.</h2>
            <p className="mt-3 text-lg font-accent italic text-secondary">I nostri host arriveranno presto.</p>
            <p className="mt-4 text-sm text-slate-500">Prova a cambiare date o destinazione, oppure torna a scoprire le esperienze.</p>
            <Link to="/esperienze" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-3">Scopri le esperienze</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map((a) => (
              <Link key={a.id} to={`/alloggi/${a.slug}${params.toString() ? "?" + params.toString() : ""}`} data-testid={`acc-card-${a.slug}`}
                className="group rounded-2xl bg-white border border-slate-100 shadow-sm hover-lift overflow-hidden">
                <div className="relative h-52 overflow-hidden bg-slate-100">
                  {a.cover && <img src={a.cover} alt={a.name} className="h-full w-full object-cover img-zoom" loading="lazy" />}
                  {a.sea_view && <span className="absolute top-3 left-3 rounded-full bg-secondary text-white text-[10px] font-bold px-3 py-1 flex items-center gap-1"><Waves className="h-3 w-3" /> Vista mare</span>}
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{a.type?.replace("_", " ")} · {a.city}</div>
                  <div className="font-heading font-semibold text-slate-900 text-lg mt-1 leading-tight">{a.name}</div>
                  <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {a.max_guests} ospiti</span>
                    {a.wifi && <Wifi className="h-3.5 w-3.5" />}
                    {a.pool && <Waves className="h-3.5 w-3.5" />}
                    {a.parking && <Car className="h-3.5 w-3.5" />}
                  </div>
                  {a.from_price != null && <div className="mt-3 text-sm"><span className="text-slate-400">da </span><span className="font-bold text-primary text-lg">{eur(a.from_price)}</span><span className="text-slate-400"> / notte</span></div>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <SponsorSlot placement="ALLOGGI" className="pb-14" />
    </Layout>
  );
}
